import {
  fmtEur,
  payrollHistory,
  payrollRows,
  splitAmount,
  type PayrollRow,
} from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

const issueLabels: Record<string, string> = {
  duplicate: "Duplicate beneficiary",
  "missing-iban": "Missing IBAN",
  "over-threshold": "Above approval threshold",
};

function previewRow(r: PayrollRow): string {
  const hasIssue = !!r.issue;
  const sealClass = hasIssue
    ? r.issue === "over-threshold"
      ? "is-pending"
      : "is-failed"
    : "is-success";

  return `<div class="ledger-table-row ${hasIssue ? "is-warning" : ""}" style="grid-template-columns: 50px minmax(0, 1.4fr) minmax(0, 1.6fr) 110px 110px;">
    <div class="col-date" style="font-variant-numeric: tabular-nums; font-weight: 700; color: var(--ink-secondary); font-size: 0.85rem;">
      ${r.rowNo.toString().padStart(2, "0")}
    </div>
    <div class="col-name" style="min-width: 0;">
      <strong>${r.name}</strong>
      <span>${r.role}</span>
    </div>
    <div class="col-name" style="min-width: 0;">
      <strong style="font-family: ui-monospace, monospace; font-size: 0.78rem; font-weight: 500; color: ${r.iban ? "var(--ink-secondary)" : "var(--state-danger)"};">${r.iban || "— missing IBAN —"}</strong>
      <span>${r.notes}</span>
    </div>
    <div class="col-amount is-out" style="font-size: 0.85rem;">
      ${fmtEur(r.amountEur)}
    </div>
    <div class="col-status">
      <span class="status-seal ${sealClass}"></span>
      ${hasIssue ? issueLabels[r.issue!] : "OK"}
    </div>
  </div>${
    hasIssue
      ? `<div class="payroll-issue payroll-issue--${r.issue}">
          <span class="payroll-issue-icon">⚠</span>
          <span><strong>Row ${r.rowNo}</strong> · ${r.issueText}</span>
          <button type="button" class="payroll-issue-action">Resolve</button>
        </div>`
      : ""
  }`;
}

export function renderPayroll(ctx: Ctx) {
  const okRows = payrollRows.filter((r) => !r.issue);
  const warnRows = payrollRows.filter((r) => !!r.issue);
  const okTotal = okRows.reduce((a, r) => a + r.amountEur, 0);
  const allTotal = payrollRows.reduce((a, r) => a + r.amountEur, 0);
  const { whole, frac } = splitAmount(allTotal);
  const fee = payrollRows.length * 0.3; // €0.30 SEPA Instant per row
  const headcount = payrollRows.length;

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Business Account<span class="page-eyebrow-rule"></span>Payroll</span>
        <h1 class="page-title">Run salaries with one upload</h1>
      </div>
      <div class="page-head-meta">
        <span class="page-head-date">Cycle</span>
        <span style="font-size: 0.85rem; font-weight: 700; color: var(--ink-primary); font-variant-numeric: tabular-nums;">May 2026 · monthly</span>
        <span style="font-size: 0.7rem; color: var(--ink-subtle);">Scheduled execution · 30 May, 09:00 CET</span>
      </div>
    </section>

    <section class="grid-2-1">
      <article class="panel" id="upload-panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Step 1</div>
            <h2 class="panel-title">Upload payroll CSV</h2>
            <p class="panel-sub">Columns required: <strong>name, role, iban, amount_eur, notes</strong>. Header row is detected automatically.</p>
          </div>
          <button class="btn btn-ghost btn-sm" type="button">${icon("download", 11)} Download template</button>
        </div>
        <div class="panel-body">
          <label class="dropzone" id="dropzone">
            <input type="file" accept=".csv,text/csv" id="csv-input" hidden />
            <div class="dropzone-mark"></div>
            <div class="dropzone-body">
              <div class="dropzone-eyebrow">Drag &amp; drop your CSV here</div>
              <div class="dropzone-title">…or click to browse</div>
              <div class="dropzone-sub">UTF-8 · up to 500 rows · IBAN-validated on parse</div>
            </div>
            <button type="button" class="btn btn-primary btn-sm" id="load-sample">Load sample file</button>
          </label>
          <div class="dropzone-meta" id="file-meta" hidden>
            <div class="dropzone-meta-icon">${icon("download", 16)}</div>
            <div class="dropzone-meta-info">
              <strong id="file-name">athena_payroll_may_2026.csv</strong>
              <span id="file-size">14 rows · 1.4 KB · uploaded 09:18 CET</span>
            </div>
            <div class="dropzone-meta-status" id="file-status">
              <span class="status-seal is-pending"></span> Validating…
            </div>
            <button class="btn btn-ghost btn-sm" type="button" id="file-remove">Remove</button>
          </div>
        </div>
      </article>

      <article class="actions-panel" id="totals-panel">
        <span class="actions-panel-watermark" aria-hidden="true"></span>
        <span class="actions-panel-eyebrow">Step 2 · Sign-off</span>
        <h2 class="actions-panel-title">Totals at a <em>glance.</em></h2>

        <div class="totals-grid">
          <div class="total-line">
            <span class="total-line-label">Headcount</span>
            <span class="total-line-value" id="t-headcount">—</span>
          </div>
          <div class="total-line">
            <span class="total-line-label">Gross total</span>
            <span class="total-line-value" id="t-gross">—</span>
          </div>
          <div class="total-line">
            <span class="total-line-label">Issues to resolve</span>
            <span class="total-line-value" id="t-issues">—</span>
          </div>
          <div class="total-line">
            <span class="total-line-label">Fee · SEPA × ${headcount}</span>
            <span class="total-line-value" id="t-fee">—</span>
          </div>
          <div class="total-line is-net">
            <span class="total-line-label">Will debit operational account</span>
            <span class="total-line-value">
              <span class="total-line-cur">€</span>
              <span id="t-net-whole">—</span>
              <span class="total-line-frac" id="t-net-frac"></span>
            </span>
          </div>
        </div>

        <p class="payroll-warning" id="payroll-warning">No file uploaded yet.</p>

        <button class="btn btn-primary btn-block" id="sign-btn" disabled>
          Sign &amp; queue payroll ${icon("arrowRight", 11)}
        </button>
      </article>
    </section>

    <section class="panel" id="preview-panel" style="margin-top: 1.5rem; display: none;">
      <div class="panel-head">
        <div>
          <div class="panel-eyebrow">Preview · row by row</div>
          <h2 class="panel-title">Each line will become one SEPA transfer</h2>
          <p class="panel-sub"><span style="color: var(--state-success); font-weight: 700;">${okRows.length} OK</span> · <span style="color: var(--state-warning); font-weight: 700;">${warnRows.length} need attention</span></p>
        </div>
        <div class="pill-toggle" id="filter-rows">
          <button class="is-active" data-filter="all">All</button>
          <button data-filter="ok">OK</button>
          <button data-filter="warning">Issues</button>
        </div>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 50px minmax(0, 1.4fr) minmax(0, 1.6fr) 110px 110px;">
          <span>#</span>
          <span>Beneficiary</span>
          <span>IBAN / notes</span>
          <span style="text-align: right;">Amount</span>
          <span>Validation</span>
        </div>
        <div id="preview-rows"></div>
        <div class="ledger-table-foot">
          <span>Showing ${headcount} rows · gross total ${fmtEur(allTotal, { compact: true })}</span>
          <span style="font-size: 0.65rem; color: var(--ink-subtle); letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700;">EUR</span>
        </div>
      </div>
    </section>

    <section class="panel" style="margin-top: 1.5rem;">
      <div class="panel-head">
        <div>
          <div class="panel-eyebrow">History</div>
          <h2 class="panel-title">Recent payroll runs</h2>
        </div>
        <a class="link-arrow" data-route="business-statements">All statements ${icon("arrowRight", 13)}</a>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 110px 1fr 100px minmax(0, 1fr) 130px 110px;">
          <span>Run date</span>
          <span>ID</span>
          <span>Headcount</span>
          <span>Operator</span>
          <span style="text-align: right;">Amount</span>
          <span>Status</span>
        </div>
        ${payrollHistory
          .map(
            (h) => `<div class="ledger-table-row" style="grid-template-columns: 110px 1fr 100px minmax(0, 1fr) 130px 110px;">
          <div class="col-date" style="font-variant-numeric: tabular-nums; font-weight: 600; color: var(--ink-secondary); font-size: 0.82rem;">${h.runDate}</div>
          <div class="col-name"><strong>${h.id}</strong></div>
          <div class="col-method" style="color: var(--ink-secondary); font-weight: 600; font-variant-numeric: tabular-nums;">${h.headcount}</div>
          <div class="col-name"><strong style="font-weight: 500; color: var(--ink-secondary);">${h.operator}</strong></div>
          <div class="col-amount is-out">${fmtEur(h.totalEur)}</div>
          <div class="col-status"><span class="status-seal is-${h.status}"></span> ${h.statusLabel}</div>
        </div>`,
          )
          .join("")}
      </div>
    </section>
  `;

  const dropzone = ctx.canvas.querySelector<HTMLElement>("#dropzone")!;
  const fileMeta = ctx.canvas.querySelector<HTMLElement>("#file-meta")!;
  const fileStatus = ctx.canvas.querySelector<HTMLElement>("#file-status")!;
  const previewPanel = ctx.canvas.querySelector<HTMLElement>("#preview-panel")!;
  const previewRows = ctx.canvas.querySelector<HTMLElement>("#preview-rows")!;
  const signBtn = ctx.canvas.querySelector<HTMLButtonElement>("#sign-btn")!;
  const warning = ctx.canvas.querySelector<HTMLElement>("#payroll-warning")!;

  function loadFile() {
    dropzone.style.display = "none";
    fileMeta.hidden = false;
    previewPanel.style.display = "";
    previewRows.innerHTML = payrollRows.map(previewRow).join("");

    // Update totals
    const headcountEl = ctx.canvas.querySelector<HTMLElement>("#t-headcount");
    const grossEl = ctx.canvas.querySelector<HTMLElement>("#t-gross");
    const issuesEl = ctx.canvas.querySelector<HTMLElement>("#t-issues");
    const feeEl = ctx.canvas.querySelector<HTMLElement>("#t-fee");
    const netWhole = ctx.canvas.querySelector<HTMLElement>("#t-net-whole");
    const netFrac = ctx.canvas.querySelector<HTMLElement>("#t-net-frac");

    if (headcountEl) headcountEl.textContent = headcount.toString();
    if (grossEl) grossEl.textContent = fmtEur(allTotal, { compact: false });
    if (issuesEl) issuesEl.textContent = warnRows.length.toString();
    if (feeEl) feeEl.textContent = fmtEur(fee, { compact: false });
    if (netWhole) netWhole.textContent = whole;
    if (netFrac) netFrac.textContent = frac;

    // Validation pulse → outcome
    setTimeout(() => {
      if (warnRows.length === 0) {
        fileStatus.innerHTML = `<span class="status-seal is-success"></span> All rows OK`;
        warning.innerHTML = `Ready to sign and queue. ${headcount} SEPA Instant transfers will be sent at <strong>30 May, 09:00 CET</strong>.`;
        signBtn.disabled = false;
      } else {
        fileStatus.innerHTML = `<span class="status-seal is-pending"></span> ${warnRows.length} issue${warnRows.length === 1 ? "" : "s"}`;
        warning.innerHTML = `${warnRows.length} row${warnRows.length === 1 ? "" : "s"} need${warnRows.length === 1 ? "s" : ""} attention before sign-off. Issues are flagged inline in the preview below.`;
        warning.classList.add("is-warning");
        signBtn.disabled = false; // soft-block — copy says "force-sign-off"
        signBtn.innerHTML = `Override &amp; sign anyway ${icon("arrowRight", 11)}`;
      }
    }, 800);
  }

  function unloadFile() {
    dropzone.style.display = "";
    fileMeta.hidden = true;
    previewPanel.style.display = "none";
    previewRows.innerHTML = "";
    signBtn.disabled = true;
    signBtn.innerHTML = `Sign &amp; queue payroll ${icon("arrowRight", 11)}`;
    warning.classList.remove("is-warning");
    warning.textContent = "No file uploaded yet.";
    [
      "#t-headcount",
      "#t-gross",
      "#t-issues",
      "#t-fee",
      "#t-net-whole",
    ].forEach((sel) => {
      const el = ctx.canvas.querySelector<HTMLElement>(sel);
      if (el) el.textContent = "—";
    });
    const netFrac = ctx.canvas.querySelector<HTMLElement>("#t-net-frac");
    if (netFrac) netFrac.textContent = "";
  }

  // Click "Load sample"
  ctx.canvas
    .querySelector<HTMLElement>("#load-sample")
    ?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      loadFile();
    });

  // File input
  ctx.canvas
    .querySelector<HTMLInputElement>("#csv-input")
    ?.addEventListener("change", () => {
      loadFile();
    });

  // Drag & drop visual feedback
  dropzone.addEventListener("dragover", (e) => {
    e.preventDefault();
    dropzone.classList.add("is-drag");
  });
  dropzone.addEventListener("dragleave", () => {
    dropzone.classList.remove("is-drag");
  });
  dropzone.addEventListener("drop", (e) => {
    e.preventDefault();
    dropzone.classList.remove("is-drag");
    loadFile();
  });

  ctx.canvas
    .querySelector<HTMLElement>("#file-remove")
    ?.addEventListener("click", unloadFile);

  // Filter pill toggle
  ctx.canvas
    .querySelectorAll<HTMLButtonElement>("#filter-rows button")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        ctx.canvas
          .querySelectorAll("#filter-rows .is-active")
          .forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
        const f = btn.dataset.filter;
        const filtered =
          f === "all"
            ? payrollRows
            : f === "ok"
              ? okRows
              : warnRows;
        previewRows.innerHTML = filtered.map(previewRow).join("");
      });
    });

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });
}
